import { useEffect, useState } from "react";
import AppForm from "../common/AppForm";
import AppLoader from "../../components/common/AppLoader";
import AppModal from "../common/AppModal";
import AppNavbar from "../../components/common/Navbar";
import VariantsList from "../common/VariantsList";
import { variantForm } from "../../constants";
import { useAsync } from "../../hooks";
import { GroupedVariants, VariantData } from "../../interface";
import { getVariants, updateVariantUrl } from "../../services";
import { variantsSchema } from "../../utils";
import { useUser } from "../../context/UserContext";
import LoggersList from "../common/LoggersList";

interface VariantsProps {
  productId: number;
  productName: string;
  handleBack: () => void;
}

const Variants = ({ productId, productName, handleBack }: VariantsProps) => {
  const { user } = useUser();
  const canEdit = user?.user_role !== "viewer";

  const [groupedByUrl, setGroupedByUrl] = useState<Record<string, VariantData[]>>({});

  // Restore expanded URLs from localStorage
  const [expandedUrls, setExpandedUrls] = useState<Set<string>>(() => {
    const saved = localStorage.getItem(`expandedUrls-${productId}`);
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });

  // Restore selected tab from localStorage
  const [selectedTab, setSelectedTab] = useState<"variants" | "loggers">(() => {
    return (
      (localStorage.getItem(`selectedTab-${productId}`) as
        | "variants"
        | "loggers") || "variants"
    );
  });

  const [openModal, setOpenModal] = useState(false);
  const [selectedUrl, setSelectedUrl] = useState<VariantData | null>(null);

  const { execute, data, loading } = useAsync<GroupedVariants[], [number]>();
  const { loading: loadingPatch, execute: executePatch } = useAsync<string, [number, any]>();

  useEffect(() => {
    if (Number.isInteger(productId)) {
      execute(getVariants, productId);
    } else {
      console.error("Invalid productId");
    }
  }, [productId, execute]);

  useEffect(() => {
    if (data) {
      // Convert GroupedVariants[] to Record<string, VariantData[]>
      const grouped = data.reduce((acc: Record<string, VariantData[]>, group) => {
        if (group.variants.some((v) => v.currentPrice != null)) {
          acc[group.url] = group.variants;
        }
        return acc;
      }, {});

      setGroupedByUrl(grouped);

      // Restore expanded URLs, but filter out URLs that no longer exist
      const savedUrls = localStorage.getItem(`expandedUrls-${productId}`);
      const initialExpanded = savedUrls
        ? new Set(
            (JSON.parse(savedUrls) as string[]).filter((url) =>
              Object.keys(grouped).includes(url)
            )
          )
        : new Set(Object.keys(grouped));

      setExpandedUrls(initialExpanded);
    }
  }, [data, productId]);

  // Persist expanded URLs
  useEffect(() => {
    localStorage.setItem(`expandedUrls-${productId}`, JSON.stringify(Array.from(expandedUrls)));
  }, [expandedUrls, productId]);

  // Persist selected tab
  useEffect(() => {
    localStorage.setItem(`selectedTab-${productId}`, selectedTab);
  }, [selectedTab, productId]);

  const handleCloseModal = () => {
    setSelectedUrl(null);
    setOpenModal(false);
  };

  const handleOpenModal = (e: React.MouseEvent, url: string) => {
    e.stopPropagation();
    if (!canEdit) return; // viewers cannot open modal
    const firstVariant = groupedByUrl[url]?.[0];
    if (firstVariant) {
      setSelectedUrl(firstVariant);
      setOpenModal(true);
    }
  };

  const handleSubmit = async (values: { url: string }) => {
    if (!selectedUrl || !user) return;
    try {
      await executePatch(updateVariantUrl, selectedUrl.variantId, {
        url: values.url,
        user: user,
      });
      await execute(getVariants, productId);
      handleCloseModal();
    } catch (err) {
      console.error("Failed to update variant URL:", err);
    }
  };

  const toggleUrlExpansion = (url: string) => {
    const newExpanded = new Set(expandedUrls);
    if (newExpanded.has(url)) newExpanded.delete(url);
    else newExpanded.add(url);
    setExpandedUrls(newExpanded);
  };

  return (
    <>
      {(loading || loadingPatch) && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <AppLoader />
        </div>
      )}
      {openModal && (
        <AppModal
          title="Edit Url"
          open={openModal}
          handleClose={handleCloseModal}
          action="Update Url"
        >
          <AppForm
            inputFields={variantForm}
            initialValues={{ url: selectedUrl?.url || "" }}
            schema={variantsSchema}
            handleSubmit={handleSubmit}
          />
        </AppModal>
      )}

      <div className="min-vh-100 bg-light">
        <AppNavbar
          onBack={handleBack}
          selectedTab={selectedTab}
          onTabChange={(tab) => {
            if (tab === "loggers" && !canEdit) return;
            setSelectedTab(tab);
          }}
          title={productName}
        />

        <div className="container-fluid py-4">
          {selectedTab === "variants" && (
            <VariantsList
              groupedByUrl={groupedByUrl}
              expandedUrls={expandedUrls}
              toggleUrlExpansion={toggleUrlExpansion}
              handleOpenModal={handleOpenModal}
              canEdit={canEdit}
              productId={productId} 
            />
          )}
          {selectedTab === "loggers" && canEdit && <LoggersList productId={productId} />}
        </div>
      </div>
    </>
  );
};

export default Variants;